// Настройки → Уведомления: включение на этом устройстве (разрешение браузера, подписка),
// тестовое уведомление, виды со своим временем, список устройств с удалением.

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { errorMessage } from '@/api/client'
import { Badge, Button, Card, EmptyState, ErrorText, IconButton, PageSpinner, useToast } from '@/components/ui'
import { formatDateTime, pluralRu } from '@/lib/format'

import { pushApi, pushKeys, usePushDevices, usePushPrefs, useRemoveDevice, useSavePrefs, useTestPush, type KindPref } from './api'
import {
  currentEnv,
  currentSubscription,
  detectSupport,
  deviceLabel,
  rememberDeviceId,
  rememberedDeviceId,
  shortTime,
  subscribeDevice,
  unsubscribeDevice,
  type PushSupport,
} from './pushClient'

const WEEKDAYS = ['понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота', 'воскресенье']

type DeviceState = 'checking' | 'on' | 'off'

function permissionNow(): NotificationPermission | 'unsupported' {
  return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission
}

function useThisDevice(support: PushSupport) {
  const client = useQueryClient()
  const [state, setState] = useState<DeviceState>(support === 'supported' ? 'checking' : 'off')
  const [permission, setPermission] = useState(permissionNow)

  useEffect(() => {
    if (support !== 'supported') return
    let alive = true
    currentSubscription()
      .then((sub) => alive && setState(sub ? 'on' : 'off'))
      .catch(() => alive && setState('off'))
    return () => {
      alive = false
    }
  }, [support])

  const enable = useMutation({
    mutationFn: async () => {
      const result = await Notification.requestPermission()
      setPermission(result)
      if (result !== 'granted') throw new Error('denied')
      const { public_key } = await pushApi.vapidKey()
      const subscription = await subscribeDevice(public_key)
      const env = currentEnv()
      const device = await pushApi.subscribe({ ...subscription, device_label: deviceLabel(env.userAgent, env.maxTouchPoints) })
      rememberDeviceId(device.id)
      return device
    },
    onSuccess: () => setState('on'),
    onSettled: () => client.invalidateQueries({ queryKey: pushKeys.devices }),
  })

  const disable = useMutation({
    mutationFn: async () => {
      const endpoint = await unsubscribeDevice()
      if (endpoint) await pushApi.unsubscribe(endpoint)
      rememberDeviceId(null)
    },
    onSuccess: () => setState('off'),
    onSettled: () => client.invalidateQueries({ queryKey: pushKeys.devices }),
  })

  return { state, permission, enable, disable }
}

function enableError(error: unknown): string | null {
  if (!error) return null
  if (error instanceof Error && error.message === 'denied') {
    return 'Браузер не разрешил уведомления. Разреши их для этого сайта в настройках браузера и попробуй снова.'
  }
  if (error instanceof Error && !('status' in error)) return error.message
  return errorMessage(error, 'Не получилось включить уведомления')
}

function IosHint() {
  return (
    <Card title="Сначала добавь на экран «Домой»" subtitle="На iPhone и iPad уведомления приходят только приложению с экрана «Домой».">
      <ol className="grid list-decimal gap-1 pl-5 text-sm text-fg">
        <li>Открой этот сайт в Safari.</li>
        <li>Нажми «Поделиться» и выбери «На экран „Домой“».</li>
        <li>Открой приложение с иконки на экране «Домой» и вернись сюда — кнопка включения появится.</li>
      </ol>
      <p className="mt-2 text-sm text-muted">Нужна iOS 16.4 или новее.</p>
    </Card>
  )
}

function ThisDeviceCard({ support }: { support: PushSupport }) {
  const toast = useToast()
  const device = useThisDevice(support)
  const test = useTestPush()

  if (support === 'ios-needs-install') return <IosHint />
  if (support === 'unsupported') {
    return (
      <Card title="Это устройство">
        <p className="text-sm text-muted">Этот браузер не поддерживает push-уведомления. Попробуй Chrome, Edge, Firefox или Safari.</p>
      </Card>
    )
  }

  const on = device.state === 'on'
  const blocked = device.permission === 'denied'
  return (
    <Card
      title="Это устройство"
      actions={<Badge tone={on ? 'accent' : 'neutral'}>{device.state === 'checking' ? 'проверяю…' : on ? 'включены' : 'выключены'}</Badge>}
    >
      {blocked && !on ? (
        <p className="mb-3 text-sm text-danger">
          Уведомления для этого сайта запрещены в браузере. Разреши их в настройках сайта (значок замка в адресной строке) и
          обнови страницу.
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        {on ? (
          <>
            <Button
              variant="primary"
              loading={test.isPending}
              onClick={() =>
                test.mutate(undefined, {
                  onSuccess: (res) =>
                    toast.show({
                      message:
                        res.sent > 0
                          ? `Отправлено на ${res.sent} ${pluralRu(res.sent, ['устройство', 'устройства', 'устройств'])}`
                          : 'Ни одно устройство не приняло уведомление',
                    }),
                })
              }
            >
              Отправить тестовое
            </Button>
            <Button variant="ghost" loading={device.disable.isPending} onClick={() => device.disable.mutate()}>
              Выключить на этом устройстве
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            loading={device.enable.isPending}
            disabled={device.state === 'checking'}
            onClick={() => device.enable.mutate()}
          >
            Включить уведомления
          </Button>
        )}
      </div>
      {device.enable.error ? <p className="mt-3 text-sm text-danger">{enableError(device.enable.error)}</p> : null}
      <ErrorText error={device.disable.error ?? test.error} className="mt-3" />
    </Card>
  )
}

function eventHint(pref: KindPref, adviceTime: string, weeklyDow: number, weeklyTime: string): string | null {
  if (pref.kind === 'advice_ready') return `Совет готовится в ${shortTime(adviceTime)}.`
  if (pref.kind === 'weekly_ready') return `Отчёт готовится: ${WEEKDAYS[weeklyDow] ?? 'понедельник'}, ${shortTime(weeklyTime)}.`
  return null
}

function KindRow({
  pref,
  hint,
  saving,
  onChange,
}: {
  pref: KindPref
  hint: string | null
  saving: boolean
  onChange: (change: { enabled?: boolean; time?: string }) => void
}) {
  const [time, setTime] = useState(shortTime(pref.time))
  const id = `notify-${pref.kind}`
  return (
    <li className="flex flex-wrap items-start gap-x-4 gap-y-2 py-3">
      <label htmlFor={id} className="flex min-h-11 min-w-0 flex-1 cursor-pointer items-start gap-3">
        <input
          id={id}
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0 accent-[var(--accent)]"
          checked={pref.enabled}
          disabled={saving}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-fg">{pref.label}</span>
          <span className="block text-sm text-muted">{pref.description}</span>
          {hint ? <span className="block text-sm text-muted">{hint}</span> : null}
        </span>
      </label>
      {pref.scheduled ? (
        <input
          type="time"
          aria-label={`Время: ${pref.label}`}
          className="h-11 rounded-lg border border-border bg-surface px-3 text-sm tabular-nums text-fg disabled:opacity-50"
          value={time}
          disabled={!pref.enabled || saving}
          onChange={(e) => setTime(e.target.value)}
          onBlur={() => {
            if (time && time !== shortTime(pref.time)) onChange({ time })
          }}
        />
      ) : null}
    </li>
  )
}

function KindsCard() {
  const prefs = usePushPrefs()
  const save = useSavePrefs()
  const toast = useToast()

  if (prefs.isPending) return <PageSpinner />
  if (prefs.isError) return <ErrorText error={prefs.error} />
  const data = prefs.data
  return (
    <Card title="Какие уведомления присылать" subtitle="Время — по часовому поясу из настроек. Каждый вид приходит не чаще раза в день.">
      <ul className="divide-y divide-border">
        {data.kinds.map((pref) => (
          <KindRow
            // Новое время с сервера — новый ключ, поле берёт его заново.
            key={`${pref.kind}-${pref.time ?? ''}`}
            pref={pref}
            hint={eventHint(pref, data.advice_time, data.weekly_report_dow, data.weekly_report_time)}
            saving={save.isPending}
            onChange={(change) =>
              save.mutate({ [pref.kind]: change }, { onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }) })
            }
          />
        ))}
      </ul>
    </Card>
  )
}

function DevicesCard() {
  const devices = usePushDevices()
  const remove = useRemoveDevice()
  const toast = useToast()
  const mine = rememberedDeviceId()

  return (
    <Card title="Устройства" subtitle="Куда приходят уведомления. Удалённое устройство можно включить снова с него самого.">
      {devices.isPending ? (
        <PageSpinner />
      ) : devices.isError ? (
        <ErrorText error={devices.error} />
      ) : devices.data.length === 0 ? (
        <EmptyState title="Пока ни одного устройства" description="Включи уведомления на телефоне или компьютере." />
      ) : (
        <ul className="divide-y divide-border">
          {devices.data.map((d) => (
            <li key={d.id} className="flex items-center gap-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-fg">
                  {d.device_label || d.service}
                  {d.id === mine ? <Badge tone="accent">это устройство</Badge> : null}
                  {d.failed_count > 0 ? <Badge tone="warning">не доставлено: {d.failed_count}</Badge> : null}
                </div>
                <div className="text-sm text-muted">
                  с {formatDateTime(d.created_at)}
                  {d.last_ok_at ? ` · последнее доставлено ${formatDateTime(d.last_ok_at)}` : ''}
                </div>
              </div>
              <IconButton
                icon="close"
                label={`Удалить ${d.device_label || 'устройство'}`}
                disabled={remove.isPending}
                onClick={() =>
                  remove.mutate(d.id, {
                    onSuccess: () => {
                      if (d.id === mine) rememberDeviceId(null)
                      toast.show({ message: 'Устройство удалено' })
                    },
                    onError: (err) => toast.show({ message: errorMessage(err), tone: 'danger' }),
                  })
                }
              />
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}

export function NotificationsPage({ support = detectSupport(currentEnv()) }: { support?: PushSupport }) {
  return (
    <div className="grid max-w-3xl gap-4">
      <ThisDeviceCard support={support} />
      <KindsCard />
      <DevicesCard />
    </div>
  )
}
